import { Activity, ChevronDown, X } from 'lucide-react';
import { useState } from 'react';
import { PageHeader } from '../components/AppShell';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Empty } from '../components/ui/empty';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Spinner } from '../components/ui/spinner';
import { useActivity, useActivitySummary } from '../lib/api/activity';
import { fmtDateTime, fmtRelative } from '../lib/date';
import type { ActivityListItem } from '../lib/types';

const ENTITY_TYPES = [
  'all',
  'programme',
  'episode',
  'channel',
  'event',
  'user',
  'push',
  'youtube',
  'live_stream',
] as const;
type EntityType = (typeof ENTITY_TYPES)[number];

/**
 * Cursor-paginated audit feed. The list endpoint supports `beforeCreatedAt`
 * which we accumulate as the user pages back through history; the previous
 * pages stay in the list so the operator can scroll through a session-long
 * timeline without re-fetching.
 */
export default function ActivityPage(): JSX.Element {
  const [entityType, setEntityType] = useState<EntityType>('all');
  const [actionFilter, setActionFilter] = useState('');
  const [pages, setPages] = useState<{ before?: string }[]>([{}]);
  const lastBefore = pages[pages.length - 1]?.before;

  const list = useActivity({
    beforeCreatedAt: lastBefore,
    entityType: entityType === 'all' ? undefined : entityType,
    action: actionFilter.trim() || undefined,
    limit: 50,
  });

  const summary = useActivitySummary(7 * 24);

  return (
    <>
      <Topbar title="Activity" />
      <PageHeader
        title="Activity log"
        subtitle="Who did what across the admin panel — last 7 days summary, full history below."
      />
      <div className="space-y-6 px-6 pb-10">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-brand" />
                <CardTitle>Last 7 days</CardTitle>
              </div>
              <CardDescription>Total actions recorded.</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-semibold tabular-nums">
                {summary.data?.total?.toLocaleString() ?? '—'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Top actions</CardTitle>
              <CardDescription>What's been busy.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {summary.data?.byAction && summary.data.byAction.length > 0 ? (
                <ul className="divide-y divide-line">
                  {summary.data.byAction.slice(0, 8).map((row) => (
                    <li
                      key={row.action}
                      className="flex items-center justify-between px-5 py-2 text-sm"
                    >
                      <button
                        type="button"
                        className="font-mono text-xs text-ink hover:text-brand"
                        onClick={() => {
                          setActionFilter(row.action);
                          setPages([{}]);
                        }}
                      >
                        {row.action}
                      </button>
                      <span className="tabular-nums text-ink-muted">
                        {row.count.toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-5 py-4 text-sm text-ink-muted">Nothing yet.</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Top actors</CardTitle>
              <CardDescription>Who's most active.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {summary.data?.byActor && summary.data.byActor.length > 0 ? (
                <ul className="divide-y divide-line">
                  {summary.data.byActor.slice(0, 8).map((row) => (
                    <li
                      key={row.userId ?? 'token'}
                      className="flex items-center justify-between px-5 py-2 text-sm"
                    >
                      <span className="text-ink">
                        {row.name ?? (row.userId ? `User ${row.userId}` : 'admin_token')}
                      </span>
                      <span className="tabular-nums text-ink-muted">
                        {row.count.toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-5 py-4 text-sm text-ink-muted">Nothing yet.</p>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <CardTitle>History</CardTitle>
                <CardDescription>
                  Cursor-paginated. Filtering resets the cursor to the top.
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <Label htmlFor="activity-entity">Entity</Label>
                  <Select
                    value={entityType}
                    onValueChange={(v) => {
                      setEntityType(v as EntityType);
                      setPages([{}]);
                    }}
                  >
                    <SelectTrigger id="activity-entity" className="w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ENTITY_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t === 'all' ? 'All entities' : t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="relative">
                  <Label htmlFor="activity-action">Action</Label>
                  <Input
                    id="activity-action"
                    value={actionFilter}
                    onChange={(e) => {
                      setActionFilter(e.target.value);
                      setPages([{}]);
                    }}
                    placeholder="e.g. push:queue"
                    className="w-[200px] font-mono"
                  />
                  {actionFilter ? (
                    <button
                      type="button"
                      onClick={() => {
                        setActionFilter('');
                        setPages([{}]);
                      }}
                      className="absolute right-2 top-[34px] -translate-y-1/2 rounded p-1 text-ink-subtle hover:text-ink"
                      aria-label="Clear filter"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {list.isLoading ? (
              <div className="p-6">
                <Spinner label="Loading…" />
              </div>
            ) : (list.data?.items.length ?? 0) === 0 ? (
              <Empty title="No matching activity" />
            ) : (
              <table className="table-base">
                <thead>
                  <tr>
                    <th className="w-44">When</th>
                    <th className="w-44">Action</th>
                    <th className="w-44">Actor</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data?.items.map((row) => <ActivityRow key={row.id} row={row} />)}
                </tbody>
              </table>
            )}
            {list.data?.hasMore ? (
              <div className="flex justify-center border-t border-line py-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (list.data?.nextBeforeCreatedAt) {
                      setPages((prev) => [...prev, { before: list.data.nextBeforeCreatedAt ?? undefined }]);
                    }
                  }}
                >
                  Load older <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function ActivityRow({ row }: { row: ActivityListItem }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const hasMetadata = !!row.metadata && Object.keys(row.metadata).length > 0;

  return (
    <>
      <tr>
        <td className="text-sm text-ink-muted" title={fmtDateTime(row.createdAt)}>
          {fmtRelative(row.createdAt)}
        </td>
        <td>
          <Badge variant="outline" className="font-mono text-[11px]">
            {row.action}
          </Badge>
        </td>
        <td className="text-sm">
          {row.actorMode === 'token' ? (
            <span className="text-ink-subtle">admin_token</span>
          ) : (
            <span className="text-ink">
              {row.user?.name ?? row.user?.email ?? row.user?.id ?? '—'}
            </span>
          )}
        </td>
        <td className="text-sm text-ink-muted">
          <div className="flex items-start justify-between gap-3">
            <span className="break-words">{row.summary ?? '—'}</span>
            {hasMetadata ? (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="shrink-0 rounded px-2 py-0.5 text-[11px] text-ink-subtle hover:bg-surface-muted hover:text-ink"
              >
                {expanded ? 'hide' : 'metadata'}
              </button>
            ) : null}
          </div>
        </td>
      </tr>
      {expanded && hasMetadata ? (
        <tr>
          <td colSpan={4} className="bg-surface-muted/40 p-0">
            <pre className="overflow-x-auto px-5 py-3 font-mono text-[11px] text-ink-muted">
              {JSON.stringify(row.metadata, null, 2)}
            </pre>
          </td>
        </tr>
      ) : null}
    </>
  );
}
